import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ISignUpUser } from '../pages/Sign-Up/SignUpForm';
import axiosInstance from 'src/api/client';
import {
  IReviewerAccountBodyParams,
  IUserJournalSpecificKeyword,
  IUserJournalSpecificKeywordFieldConfig,
  IUserDraft
} from '@publishing-engine/domain/models';
import {
  IOrganizationForm,
  IResearchFocusAndExpertiseType,
  IUserType
} from 'src/components/UserRegistrationSteps/StepThree/signupHook';
import { IBoardMember } from '@publishing-engine/domain/types/AuthorType';
import { IUserProfileDetailsType } from 'src/components/User/ProfileDetails';

type IAxiosMethod = 'post' | 'delete' | 'put' | 'patch' | 'get';

export type IOrganizations = {
  ringgoldId: string;
  name: string;
  topLevelOrganizationName?: string;
  organizationType?: string;
  formattedLocation?: string;
  primaryDomain?: string;
  country?: string;
  countryCode?: string;
  postalCode?: string;
  city?: string;
  state?: string;
  postCode?: string;
  url?: string;
};

export type IUserDetailsResponse = {
  profileDetails: IUserProfileDetailsType;
  researchFocusAndExpertise: IResearchFocusAndExpertiseType;
  user: IUserType;
  affiliationDetails: IOrganizationForm;
  additionalAffiliations: IOrganizationForm[];
  realm: string;
  organizationId: string;
  boardMemberships?: Array<IBoardMember>;
  journals?: Array<{
    id: string;
    name: string;
    shortname: string;
    roles: string[];
  }>;
};

export type IUserJournalSpecificKeywordGroup = {
  submissionContextId: string;
  journalName: string;
  journalShortname: string;
  workspaceId: string;
  keywordFields: IUserJournalSpecificKeywordFieldConfig[];
  keywords: Array<
    IUserJournalSpecificKeyword & {
      createdAt?: string;
      updatedAt?: string;
      createdByName?: string;
      updatedByName?: string;
    }
  >;
};

export type ISearchResponseType = {
  totalCount: number;
  organizations: IOrganizations[];
};
const axiosMutationClient = <T>({
  url,
  payload,
  method = 'post',
  params
}: {
  url: string;
  payload?: unknown;
  method?: IAxiosMethod;
  params?: Record<string, string | boolean>;
}) => {
  return axiosInstance[method]<T>(url, payload, { params })
    .then(r => {
      return {
        ...(r.data as any),
        isSuccess: true as const,
        message: (r.data as any).message as string
      };
    })
    .catch(e => ({
      message: e.response.data,
      isSuccess: false as const
    }));
};

export const useRegisterUser = () => {
  const queryClient = useQueryClient();
  const queryKey = ['signup-register-user'];

  const createRegisterUserMutation = useMutation({
    mutationFn: async ({ email }: ISignUpUser) =>
      axiosMutationClient({
        url: `/users/register`,
        payload: { email },
        method: 'post'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return createRegisterUserMutation;
};

export const getUserDetails = (userId: string) => {
  return axiosInstance
    .get<{ data: IUserDraft }>(`/users/registration-details/${userId}`)
    .then(r => r.data);
};

export const getUserSignupFormSchema = (userId: string) => {
  return axiosInstance
    .get<any>(`/organizations/${userId}/user-signup-form-schema`)
    .then(r => r.data);
};

export const useUserDetailsMutation = () => {
  const queryClient = useQueryClient();
  const queryKey = ['signup-user-details'];

  const createUserDetailsMutation = useMutation({
    mutationFn: async ({ payload, userId }: { userId: string; payload: any }) =>
      axiosMutationClient({
        url: `/users/registration-draft/${userId}`,
        payload: { ...payload },
        method: 'post'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return createUserDetailsMutation;
};

export function fetchRinggoldOrganization(
  term: string,
  offset: number = 0,
  limit: number = 10
) {
  return axiosInstance
    .get<ISearchResponseType>(
      `/integrations/ringgold/organization/affiliations`,
      {
        params: {
          term,
          limit,
          offset
        },
        paramsSerializer: {
          indexes: null
        }
      }
    )
    .then(r => r.data);
}

export const getUserVerifyEmail = (token: string) => {
  return axiosInstance
    .get<any>(`/users/verify-email/${token}`)
    .then(r => r.data);
};

export const getUserVerifyPrimaryEmail = (token: string) => {
  return axiosInstance
    .get<{ message: string; userId: string; email: string }>(
      `/users/verify-primary-email/${token}`
    )
    .then(r => r.data);
};

export const getRegistrationActivation = (userId: string) => {
  return axiosInstance
    .post<any>(`users/registration-activation/${userId}`)
    .then(r => r.data);
};

export const useActivateAccountMutation = () => {
  const queryClient = useQueryClient();
  const queryKey = ['activate-account'];
  const createActivateAccountMutation = useMutation({
    mutationFn: async ({ userId }: { userId: string }) =>
      axiosMutationClient({
        url: `users/registration-activation/${userId}`,
        method: 'post'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return createActivateAccountMutation;
};

export const useAddUserMutation = () => {
  const queryClient = useQueryClient();
  const queryKey = ['create-user'];
  const createAddUserMutation = useMutation({
    mutationFn: async ({
      payload,
      emailNotify,
      workspaceId
    }: {
      payload: IReviewerAccountBodyParams;
      emailNotify: boolean;
      workspaceId: string;
    }) =>
      axiosMutationClient({
        url: `users/workspace/${workspaceId}/create-by-admin`,
        method: 'post',
        payload: { ...payload },
        params: { emailNotify }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['users-list'] });
      queryClient.invalidateQueries({ queryKey: ['user-details'] });
    }
  });

  return createAddUserMutation;
};

export const useUpdateUserByAdminMutation = () => {
  const queryClient = useQueryClient();
  const queryKey = ['update-user-by-admin'];
  const createUpdateUserByAdminMutation = useMutation({
    mutationFn: async ({
      payload,
      emailNotify,
      workspaceId
    }: {
      payload: IReviewerAccountBodyParams & { userId: string };
      emailNotify: boolean;
      workspaceId: string;
    }) =>
      axiosMutationClient({
        url: `users/workspace/${workspaceId}/update-by-admin`,
        method: 'post',
        payload: { ...payload },
        params: { emailNotify }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['users-list'] });
      queryClient.invalidateQueries({ queryKey: ['user-details'] });
    }
  });

  return createUpdateUserByAdminMutation;
};

export const fetchUserDetailsById = (userId: string) => {
  return axiosInstance
    .get<{ data: IUserDetailsResponse }>(`users/user-details/${userId}`)
    .then(r => r.data);
};

export const fetchUserJournalSpecificKeywords = (userId: string) => {
  return axiosInstance
    .get<{
      data: {
        journalSpecificKeywords: IUserJournalSpecificKeywordGroup[];
      };
    }>(`users/${userId}/journal-specific-keywords`)
    .then(r => r.data);
};

export function fetchUserProfileData() {
  return axiosInstance
    .get<{ data: IUserDetailsResponse }>('users/profile-details')
    .then(r => r.data);
}

export function fetchScandinavianKeywordFormSchema(
  submissionContextId: string
) {
  return axiosInstance
    .get<any>(
      `submission-contexts/${submissionContextId}/scandinavian-keyword-form-schema`
    )
    .then(r => r.data);
}

export function fetchJournalSpecificKeywordFormSchema(
  submissionContextId: string
) {
  return axiosInstance
    .get<any>(
      `submission-contexts/${submissionContextId}/journal-specific-keyword-form-schema`
    )
    .then(r => r.data);
}

export const useCreateJournalSpecificKeywordMutation = () => {
  const queryClient = useQueryClient();
  const createJournalSpecificKeywordMutation = useMutation({
    mutationFn: async ({
      userId,
      submissionContextId,
      payload
    }: {
      userId: string;
      submissionContextId: string;
      payload: {
        keyword?: string;
        keywordId?: string;
        scandinavianKeyword?: string;
        scandinavianKeywordId?: string;
      };
    }) =>
      axiosMutationClient({
        url: `users/${userId}/journal-specific-keywords/${submissionContextId}`,
        method: 'post',
        payload: { ...payload }
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['user-journal-specific-keywords', variables.userId]
      });
    }
  });

  return createJournalSpecificKeywordMutation;
};

export const useUpdateJournalSpecificKeywordMutation = () => {
  const queryClient = useQueryClient();
  const updateJournalSpecificKeywordMutation = useMutation({
    mutationFn: async ({
      userId,
      submissionContextId,
      keywordId,
      payload
    }: {
      userId: string;
      submissionContextId: string;
      keywordId: string;
      payload: {
        keyword?: string;
        keywordId?: string;
        scandinavianKeyword?: string;
        scandinavianKeywordId?: string;
      };
    }) =>
      axiosMutationClient({
        url: `users/${userId}/journal-specific-keywords/${submissionContextId}/${keywordId}`,
        method: 'put',
        payload: { ...payload }
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['user-journal-specific-keywords', variables.userId]
      });
    }
  });

  return updateJournalSpecificKeywordMutation;
};

export const useDeleteJournalSpecificKeywordMutation = () => {
  const queryClient = useQueryClient();
  const deleteJournalSpecificKeywordMutation = useMutation({
    mutationFn: async ({
      userId,
      submissionContextId,
      keywordId
    }: {
      userId: string;
      submissionContextId: string;
      keywordId: string;
    }) =>
      axiosMutationClient({
        url: `users/${userId}/journal-specific-keywords/${submissionContextId}/${keywordId}`,
        method: 'delete'
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['user-journal-specific-keywords', variables.userId]
      });
    }
  });

  return deleteJournalSpecificKeywordMutation;
};

export const useUpdatedUserInfoMutation = () => {
  const queryClient = useQueryClient();
  const queryKey = ['updated-user'];
  const createUpdatedUserInfoMutation = useMutation({
    mutationFn: async ({ payload }: { payload: any }) =>
      axiosMutationClient({
        url: `users/update-profile`,
        method: 'post',
        payload: { ...payload }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['user-profile-data'] });
    }
  });

  return createUpdatedUserInfoMutation;
};

export const useChangePasswordMutation = () => {
  const queryClient = useQueryClient();
  const queryKey = ['change-user-password'];
  const createUpdatedUserInfoMutation = useMutation({
    mutationFn: async ({ password }: { password: string }) =>
      axiosMutationClient({
        url: `users/change-password`,
        method: 'post',
        payload: { password }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return createUpdatedUserInfoMutation;
};

export const useForgetPasswordMutation = () => {
  const queryClient = useQueryClient();
  const queryKey = ['change-user-password'];
  const createForgetPasswordMutation = useMutation({
    mutationFn: async ({ email }: { email: string }) =>
      axiosMutationClient({
        url: `users/forgot-password`,
        method: 'post',
        payload: { email }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return createForgetPasswordMutation;
};

export const useResetPasswordMutation = () => {
  const queryClient = useQueryClient();
  const queryKey = ['change-user-password'];
  const createResetPasswordMutation = useMutation({
    mutationFn: async ({
      token,
      password
    }: {
      token: string;
      password: string;
    }) =>
      axiosMutationClient({
        url: `users/reset-password/${token}`,
        method: 'post',
        payload: { password }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return createResetPasswordMutation;
};

export const useValidateORCIDMutation = () => {
  const queryClient = useQueryClient();
  const queryKey = ['create-user'];
  const validateORCIDMutation = useMutation({
    mutationFn: async ({ orcid }: { orcid: string }) =>
      axiosMutationClient({
        url: `users/validate-orcid/${orcid} `,
        method: 'get'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return validateORCIDMutation;
};

export function fetchReviewerExpertiseSuggestionSpecialty(
  specialty: string,
  limit: number
) {
  return axiosInstance
    .get<{ data: any }>(
      `integrations/reviewer-expertise-suggestion/speciality?speciality=${specialty}&offset=0&limit=${limit}`
    )
    .then(r => r.data);
}

export function fetchReviewerExpertiseSuggestionKeywords(
  termID: string,
  limit: number
) {
  return axiosInstance
    .get<{ data: any }>(
      `integrations/reviewer-expertise-suggestion/keywords?termId=${termID}&offset=0&limit=${limit}`
    )
    .then(r => r.data);
}

export const getVerifyExpireLink = (token: string) => {
  return axiosInstance
    .get<{ isExpired: boolean; message: string; success: boolean }>(
      `/users/validate-link/${token}`
    )
    .then(r => r.data);
};
